import React, { createContext, useCallback, useContext, useState } from "react";
import { useFeatureFlag } from "@jsq/flags-react";
import { getFayAuthFailureReason } from "../utils/fayAuthErrors";
import { ActivityTabFlagProvider } from "./useActivityTabFlag";
import type { FayAuthFailureReason } from "../utils/fayAuthErrors";
import type { ApolloError } from "@apollo/client";

/**
 * Page-level gate for a dead fay session. The coverage grid, the upload rail,
 * and the package-detail slideout each poll fay independently; when the arena
 * kill-switch (or the granular permission) flips off while the page is open,
 * fay starts returning 401/403 on the next tick. Whichever poller detects that
 * first (via `isFayAuthFailure`) reports here, and the host app's page shell
 * swaps the whole page to a generic error page instead of leaving each widget to
 * render its own inline "Failed to load…" line.
 *
 * `authFailed` is state (not a ref) so the parent re-renders and swaps. It
 * latches true — a dead fay session won't recover without a fresh page load, so
 * once either poller sees auth failure the page stays gated until refresh.
 *
 * A split-context shape (mirroring useCoverageRefresh) keeps a consumer that only
 * reports from re-rendering when `authFailed` changes, and vice versa.
 *
 * The whole behavior sits behind the Fay MVP August release flag: with it off
 * the gate never arms, so every widget keeps rendering its own inline error.
 *
 * Each host app wraps its Fay surface in `FayAuthGateProvider` once.
 *
 * Pass the failing `ApolloError` when reporting: the provider derives *which*
 * of fay's three refusals it was (via `getFayAuthFailureReason`) so the host's
 * error page can name the refusal in fay's pinned words instead of one generic
 * message. Reporting without an error still latches the gate — the reason just
 * stays null and the host renders its generic copy.
 */
type ReportFn = (error?: ApolloError) => void;

const noopReport: ReportFn = () => undefined;

const ReportContext = createContext<ReportFn | null>(null);
const AuthFailedContext = createContext<boolean>(false);
const AuthFailureReasonContext = createContext<FayAuthFailureReason | null>(null);
const GateArmedContext = createContext<boolean>(false);

/**
 * Raw read of the `fayMVPAugust` release flag, keeping the key and default in
 * one place (per the `@jsq/flags-react` wrapping-hook guidance).
 *
 * Only the provider below and the host app's permission branch call this — both
 * always render under a real FlagProvider. Widgets deliberately read
 * `useFayAuthGateArmed` instead, so a widget rendered on its own doesn't drag a
 * FlagProvider requirement into its tests.
 */
export function useFayAuthGateFlag(): boolean {
  return useFeatureFlag("fayMVPAugust", false);
}

type Props = {
  children: React.ReactNode;
};

export const FayAuthGateProvider: React.FC<Props> = ({ children }) => {
  const gateArmed = useFayAuthGateFlag();
  const [authFailed, setAuthFailed] = useState(false);
  const [reason, setReason] = useState<FayAuthFailureReason | null>(null);

  const report = useCallback<ReportFn>((error) => {
    setAuthFailed(true);
    // The reason latches like the boolean, but independently: the first
    // *recognized* refusal wins, so a poller that reports an unrecognizable
    // shape first (e.g. a dead session's 401) doesn't block a sibling poller
    // from filling the reason in on its own tick.
    const derived = error ? getFayAuthFailureReason(error) : null;
    if (derived) {
      setReason((prev) => prev ?? derived);
    }
  }, []);

  // With the flag off the provider still mounts (so the tree shape doesn't
  // change with the flag) but publishes "not armed", hands out the no-op
  // reporter, and never reports a failure — leaving the pollers' pre-existing
  // `stopPolling()` as the only effect. Both reporter identities are
  // module/`useCallback`-stable, so swapping between them doesn't churn
  // consumers' effect deps.
  //
  // The Activity tab release gate is composed in at the bottom because the host
  // mounting Fay already wraps that whole surface in this provider, so the gate
  // reaches every consumer with no host-app change. The flags stay independent —
  // each publishes its own read, untouched by `gateArmed`.
  return (
    <GateArmedContext.Provider value={gateArmed}>
      <ReportContext.Provider value={gateArmed ? report : noopReport}>
        <AuthFailedContext.Provider value={gateArmed && authFailed}>
          <AuthFailureReasonContext.Provider value={gateArmed ? reason : null}>
            <ActivityTabFlagProvider>{children}</ActivityTabFlagProvider>
          </AuthFailureReasonContext.Provider>
        </AuthFailedContext.Provider>
      </ReportContext.Provider>
    </GateArmedContext.Provider>
  );
};

/** Report that fay returned a terminal auth failure. No-op outside the provider
 *  (safe in tests that render a poller in isolation). Stable identity, so it's
 *  safe to list in an effect's deps. */
export function useReportFayAuthFailure(): ReportFn {
  const report = useContext(ReportContext);
  // `noopReport` is a module-level constant, so the fallback identity stays
  // stable across renders when the provider is absent.
  return report ?? noopReport;
}

/** True once any fay poller has reported a terminal auth failure. */
export function useFayAuthFailed(): boolean {
  return useContext(AuthFailedContext);
}

/**
 * Which of fay's three terminal refusals tripped the gate, or null when none
 * did — either nothing has failed yet, or the failure carried no pinned
 * refusal copy (e.g. a dead session), in which case the host's generic error
 * is the right rendering. Render the words via `fayAuthFailureMessage(reason)`
 * so both hosts refuse in fay's one voice.
 *
 * Sibling to `useFayAuthFailed()`, not a replacement: the boolean keeps its
 * consumers, this adds the "why" for hosts that can name it.
 */
export function useFayAuthFailureReason(): FayAuthFailureReason | null {
  return useContext(AuthFailureReasonContext);
}

/**
 * True only when a `FayAuthGateProvider` is above us AND the release flag is on
 * — i.e. when something will actually swap the page on an auth failure.
 *
 * Widgets check this before suppressing their own inline error: outside the
 * provider, or with the flag off, nothing else would render an error, so the
 * inline line has to stay rather than leaving the user with a blank panel. It is
 * also the read every widget uses for the release gate itself, so a widget
 * rendered on its own doesn't require a FlagProvider in its tests.
 */
export function useFayAuthGateArmed(): boolean {
  return useContext(GateArmedContext);
}
